import { format } from "date-fns";
import {
  Check,
  ChevronDownIcon,
  Edit,
  Plus,
  Trash2,
  Webcam,
} from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogFooter,
  DialogClose,
} from "@/components/ui/dialog";
import {
  Command,
  CommandEmpty,
  CommandGroup,
  CommandInput,
  CommandItem,
  CommandList,
} from "@/components/ui/command";
import { PhoneNumberUtil } from "google-libphonenumber";
import { MultiSelect } from "@/components/ui/multiselect/multiselectCheckbox";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Switch } from "@/components/ui/switch";
import React, { useEffect, useMemo, useState } from "react";
import { Button } from "@/components/ui/button";
import { FloatingLabelInput } from "@/components/ui/floatinglable/floating";
import { RxCross2 } from "react-icons/rx";
import { Controller, FormProvider, useForm } from "react-hook-form";
import * as z from "zod";
import { FormLabel, FormControl } from "@/components/ui/form";
import { toast } from "@/components/ui/use-toast";

import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from "@/components/ui/popover";
import { cn } from "@/lib/utils";
import { CalendarIcon } from "@radix-ui/react-icons";
import { Calendar } from "@/components/ui/calendar";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from "@/components/ui/tooltip";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import {
  BusinessTypes,
  CountryTypes,
  DropdownOption,
  ErrorType,
  SourceTypes,
} from "@/types";

import {
  MemberGroup,
  MemberInputTypes,
  MemberTableDatatypes,
} from "@/types/member";
import "react-international-phone/style.css"; // Import the default styles for the phone input
import { LoadingButton } from "@/components/ui/common/loading-button";
import {
  useGetAllBusinessesQuery,
  useGetMemberCountQuery,
  useAddMemberMutation,
  useUpdateMemberMutation,
  useGetMemberInSystemQuery,
} from "@/services/memberApi";

import {
  useGetAllSourceQuery,
  useGetCountriesQuery,
} from "@/services/commonApi";

import { useGetCoachesQuery, useGetCoachListQuery } from "@/services/coachApi";
import {
  useGetMembershipListQuery,
  useGetMembershipsQuery,
} from "@/services/membershipsApi";
import { deleteCognitoImage, UploadCognitoImage } from "@/lib/utils/s3Service";
import profileimg from "@/assets/profile-image.svg";
import { Separator } from "@/components/ui/separator";
import { PhoneInput } from "react-international-phone";
import { capitalizeFirstLetter, formatNIC } from "@/lib/helper";
import { Gender, InvoiceStatus, InvoiceType, ProductType } from "@/types/enums";
import { SearchComboBox } from "@/components/ui/globalsearch/search-combo-box";
import { User } from "@/types";
import { BaseItem, InvoicePayload } from "@/types/invoice";
import { useGetSalesTaxListQuery } from "@/services/salesTaxApi";
import { useGetOrgTaxTypeQuery } from "@/services/organizationApi";
import { useGetIncomeCategorListQuery } from "@/services/incomeCategoryApi";
import InvoiceForm from "../../financials/modal/form";
import { Badge } from "@/components/ui/badge";
import {
  useCreateMemberGroupMutation,
  useDeleteMemberGroupMutation,
  useGetMemberGroupsQuery,
  useUpdateMemberGroupMutation,
} from "@/services/memberGroupApis";
import { ConfirmationDialog } from "@/components/ui/common/confirmation-dailog";
import { useGetSystemSettingsQuery } from "@/services/systemConfigApi";
import Combobox from "@/components/ui/common/combobox";
const { VITE_VIEW_S3_URL } = import.meta.env;

export enum statusEnum {
  pending = "pending",
  active = "active",
  inactive = "inactive",
}
const coachsSchema = z.object({
  id: z.number(),
  name: z.string(),
});

const initialValues: MemberInputTypes = {
  own_member_id: "",
  profile_img: "",
  first_name: "",
  last_name: "",
  gender: Gender.Male,
  dob: "",
  nic: "",
  email: "",
  phone: "",
  mobile_number: "",
  notes: "",
  source_id: null,
  member_group_id: undefined,
  is_business: false,
  business_id: null,
  country_id: null,
  client_status: "pending",
  city: "",
  zipcode: "",
  address_1: "",
  address_2: "",
  coach_ids: [] as z.infer<typeof coachsSchema>[],
  send_invitation: true,
};

interface MemberFormTypes {
  open: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  memberData: MemberTableDatatypes | null;
  setMemberData: React.Dispatch<
    React.SetStateAction<MemberTableDatatypes | null>
  >;
  action: string;
  setAction: React.Dispatch<
    React.SetStateAction<"add" | "edit" | "member-created">
  >;
  refetch?: () => void;
  breadcrumb?: string;
}

const MemberForm = ({
  open,
  setOpen,
  memberData,
  setMemberData,
  action,
  setAction,
  refetch,
  breadcrumb,
}: MemberFormTypes) => {
  const hide_member_group = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .hide_member_group ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) ?? 0;
  const userInfo = useSelector((state: RootState) => state.auth.userInfo?.user);
  const orgName = useSelector(
    (state: RootState) => state.auth.userInfo?.user?.org_name
  );
  const [isDialogOpen, setIsDialogOpen] = React.useState<boolean>(false);
  const [editInvoiceData, setEditInvoiceData] = React.useState<
    InvoicePayload | undefined
  >(undefined);

  const [searchTerm, setSearchTerm] = React.useState("");
  const { data: users, isLoading: loading } = useGetMemberInSystemQuery({
    query: searchTerm,
    limit: 10,
    offset: 0,
    sort_order: "desc",
    sort_key: "id",
    type: "member",
    org_id: 0,
  }, {
    skip: !open
  });
  const [selectedImage, setSelectedImage] = React.useState<File | null>(null);

  const [avatar, setAvatar] = React.useState<string | ArrayBuffer | null>(null);
  const [country, setCountry] = useState(false);
  const [openBusiness, setOpenBusiness] = useState(false);
  const [openSource, setOpenSource] = useState(false);
  const [openGroup, setOpenGroup] = useState(false);
  const [openGroupForm, setOpenGroupForm] = useState(false);
  const [isDeleteGroup, setDeleteGroup] = useState(false);
  const [group, setGroup] = useState<DropdownOption | undefined>(undefined);
  const [formData, setFormData] = useState<MemberInputTypes>(initialValues);
  const [dob, setDob] = useState(false);

  const { data: otherSettingsData, refetch: settingsRefetch } =
    useGetSystemSettingsQuery({
      query: "groups=other_settings",
    });

  const other_settings = useMemo(
    () => otherSettingsData?.other_settings as Record<string, string>,
    [otherSettingsData]
  );

  const userOptions = useMemo(() => {
    return Array.isArray(users) ? users : [];
  }, [users]);

  // conditional fetching
  const { data: memberCountData, refetch: refecthCount } =
    useGetMemberCountQuery(orgId, {
      skip: action == "edit",
    });

  const { data: memberGroupData, refetch: refetchGroups } =
    useGetMemberGroupsQuery();

  const memberGroupOptions = useMemo(() => {
    return Array.isArray(memberGroupData?.data)
      ? memberGroupData?.data.map((group) => {
        return {
          value: group.id,
          label: group.name,
        };
      })
      : [];
  }, [memberGroupData]);

  const phoneUtil = PhoneNumberUtil.getInstance();

  const validatePhone = (value: string | undefined | null) => {
    if (!value) {
      return true; // If value is empty, validation passes (based on requirements)
    }

    if (value.length > 20) {
      return "Phone number cannot exceed 20 digits";
    }

    if (value.length <= 4) {
      return true; // Pass validation if length is 5 or fewer
    }

    try {
      // Parse the phone number with the default country code (can be adjusted)
      const parsedNumber = phoneUtil.parseAndKeepRawInput(value);

      // Check if the parsed number is a valid phone number
      if (!phoneUtil.isValidNumber(parsedNumber)) {
        return "Invalid phone number";
      }

      return true; // Return true if validation passes
    } catch (error) {
      // Catch parsing errors and return an appropriate message
      return "Invalid phone number format";
    }
  };

  const { data: countries } = useGetCountriesQuery();
  const { data: salesTaxData } = useGetSalesTaxListQuery(orgId);
  const { data: business, refetch: refetchBusiness } =
    useGetAllBusinessesQuery(orgId);
  // const { data: coachesData } = useGetCoachListQuery(orgId);


  const {
    data: coachData,
  } = useGetCoachesQuery(
    { org_id: orgId, query: "sort_key=id&sort_order=desc" },
    {
      skip: !open,
    }
  );

  const coachOptions = useMemo(() => {
    return Array.isArray(coachData?.data) ? coachData?.data.map((coach) => ({
      value: Number(coach.id),
      label: `${coach.own_coach_id} • ${coach.first_name} ${coach.last_name}`,
    })) : [];
  }, [coachData]);




  const { data: sources } = useGetAllSourceQuery();
  const { data: membershipsData } = useGetMembershipsQuery({
    org_id: orgId,
    query: "status=active",
  });

  const membershipPlansOptions = useMemo(() => {
    return Array.isArray(membershipsData?.data)
      ? membershipsData?.data.map((plan) => ({
        label: plan?.name,
        value: plan?.id?.toString(),
      }))
      : [];
  }, [membershipsData]);

  const [addMember] = useAddMemberMutation();
  const [editMember] = useUpdateMemberMutation();
  const [deleteGroup] = useDeleteMemberGroupMutation();

  const onClose = () => {
    setDeleteGroup(false);
  };

  const onConfirm = async () => {
    try {
      const response = await deleteGroup(Number(group?.value));
      if (response) {
        toast({
          variant: "success",
          title: "Group deleted successfully",
        });
        setDeleteGroup(false);
        refetchGroups();
      }
    } catch (error) {
      toast({
        title: "Error",
        description: "Failed to delete group",
      });
    }
  };

  const [membershipPlansdata, setMembershipPlansdata] = useState<BaseItem[]>([
    {
      id: 1,
      item_id: 0,
      net_price: 0,
      item_type: ProductType.Membership,
      description: "",
      income_category_id: 0,
      quantity: 0,
      sale_tax_id: 0,
      price: 0,
      tax_rate: 0,
      tax_perc: 0,
      revenue_type: "income",
      is_srb: false,
      discount: 0,
      sub_total: 0,
      tax_type: "",
      tax_name: "",
      total: 0,
      tax_amount: 0,
      booking_date: "",
    },
  ]);

  const handleAddPlan = () => {
    setMembershipPlansdata([
      ...membershipPlansdata,
      {
        id:
          membershipPlansdata?.length > 1 ? membershipPlansdata?.length + 1 : 1,
        item_id: 0,
        net_price: 0,
        item_type: ProductType.Membership,
        description: "",
        income_category_id: 0,
        quantity: 0,
        sale_tax_id: 0,
        price: 0,
        tax_rate: 0,
        discount: 0,
        sub_total: 0,
        tax_perc: 0,
        revenue_type: "income",
        is_srb: false,
        tax_type: "",
        tax_name: "",
        total: 0,
        tax_amount: 0,
        booking_date: "",
      },
    ]);
  };

  const handleRemovePlan = (index: number) => {
    const updatedPlans = [...membershipPlansdata]; // Create a shallow copy
    updatedPlans.splice(index, 1); // Remove the exact item
    console.log("After removal:", updatedPlans);

    if (updatedPlans.length === 0) {
      setMembershipPlansdata([
        {
          id: 1,
          item_id: 0,
          item_type: ProductType.Membership,
          description: "",
          income_category_id: 0,
          quantity: 0,
          sale_tax_id: 0,
          price: 0,
          tax_rate: 0,
          net_price: 0,
          discount: 0,
          sub_total: 0,
          tax_perc: 0,
          revenue_type: "income",
          is_srb: false,
          tax_type: "",
          tax_name: "",
          total: 0,
          tax_amount: 0,
          booking_date: "",
        },
      ]);
    } else {
      setMembershipPlansdata(updatedPlans); // Update the state
    }
  };
  const { data: orgTaxType } = useGetOrgTaxTypeQuery(orgId) || {
    tax_type: "exclusive",
  };
  const { data: incomeCatData } = useGetIncomeCategorListQuery(orgId);

  const handleMembershipPlanChange = (
    index: number,
    field: string,
    value: any
  ) => {
    const updatedPlans = [...membershipPlansdata];
    updatedPlans[index] = { ...updatedPlans[index], [field]: value };

    if (field === "membership_plan_id") {
      // Find the selected membership plan by name (since combobox passes the label)
      const selectedPlan = membershipsData?.data?.find(
        (plan) => plan.name === value // Compare by name, not ID
      );

      if (!selectedPlan) {
        console.error("Selected membership plan not found:", value);
        return;
      }

      // Check for duplicates across all plans using the actual ID
      const isDuplicate = updatedPlans.some(
        (plan, idx) => idx !== index && plan.item_id === selectedPlan.id
      );
      if (isDuplicate) {
        toast({
          variant: "destructive",
          title: "Duplicate Selection",
          description: "You have already selected this membership plan.",
        });
        return; // Exit the function to prevent further processing
      }

      // Now we have the correct selectedPlan, proceed with calculations
      const incomeCat = incomeCatData?.find(
        (item) => item.id === selectedPlan.income_category_id
      );
      const saleTax = salesTaxData?.find(
        (item) => item.id === incomeCat?.sale_tax_id
      );
      const taxRate = Number(selectedPlan.tax_perc);
      // Calculate discount
      const discountPercentage = selectedPlan.discount || 0;
      const discountAmount =
        Math.round(
          (((selectedPlan.net_price as number) * discountPercentage) / 100) *
          100
        ) / 100;
      const discountedPrice =
        Math.round(
          ((selectedPlan.net_price as number) - discountAmount) * 100
        ) / 100;

      // Initialize subtotal, tax, and total calculations
      let subTotal = selectedPlan.net_price; // Subtotal before discount
      let taxAmount = 0;
      let total = 0;
      if (orgTaxType?.tax_type === "inclusive") {
        // Tax-inclusive calculation, adjust for tax within discounted price
        taxAmount =
          Math.round(
            (discountedPrice - discountedPrice / (1 + taxRate / 100)) * 100
          ) / 100;
        total = discountedPrice; // Total with tax and discount included
      } else if (orgTaxType?.tax_type === "exclusive") {
        // Tax-exclusive calculation, add tax to discounted price
        taxAmount = Math.round(((discountedPrice * taxRate) / 100) * 100) / 100;
        total = Math.round((discountedPrice + taxAmount) * 100) / 100;
      }
      // Fill all base items of the selected membership data
      updatedPlans[index] = {
        ...updatedPlans[index],
        item_id: selectedPlan.id, // Now correctly set with the actual ID
        item_type: ProductType.Membership,
        description: selectedPlan.name,
        income_category_id: selectedPlan.income_category_id as number,
        quantity: 1,
        sale_tax_id: saleTax?.id as number,
        price: selectedPlan.net_price as number,
        tax_rate: taxRate,
        tax_perc: taxRate,
        revenue_type: selectedPlan.revenue_type as string,
        is_srb: selectedPlan.is_srb as boolean,
        discount: discountAmount,
        sub_total: subTotal as number,
        net_price: Math.max((subTotal as number) - discountAmount, 0),
        tax_type: orgTaxType?.tax_type as string,
        tax_name: saleTax?.name as string,
        total: total,
        tax_amount: taxAmount,
        booking_date: new Date().toISOString().split("T")[0], // Set booking date to today
      };
    }

    setMembershipPlansdata(updatedPlans);
  };

  const handleImageChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];

    const validTypes = ["image/png", "image/jpg", "image/jpeg"];
    const maxSizeMB = 1;
    const maxSizeBytes = maxSizeMB * 1024 * 1024;

    if (file) {
      if (!validTypes.includes(file.type)) {
        toast({
          variant: "destructive",
          title: "Error Uploading Image",
          description:
            "Unsupported image type. Only PNG, JPG, and JPEG are supported.",
        });
        return;
      }

      if (file.size > maxSizeBytes) {
        toast({
          variant: "destructive",
          title: "Error Uploading Image",
          description: `Image size exceeds ${maxSizeMB} MB limit.`,
        });
        return;
      }

      const reader = new FileReader();

      setSelectedImage(file);

      reader.onloadend = () => {
        setAvatar(reader.result);
      };

      reader.readAsDataURL(file);
    }
  };

  const handleSetAvatarClick = () => {
    document.getElementById("fileInput")?.click();
  };

  const form = useForm<MemberInputTypes>({
    defaultValues: initialValues,
    mode: "onChange",
  });

  const {
    control,
    watch,
    register,
    setValue,
    handleSubmit,
    clearErrors,
    reset,
    formState: { isSubmitting, errors },
  } = form;

  const watcher = watch();

  useEffect(() => {
    if (action == "edit") {
      const memberpayload = {
        ...memberData,
        nic: memberData?.nic !== null ? memberData?.nic : "",
      };
      memberpayload.coach_ids = memberData?.coaches.every(
        (item) => item.id === 0 && item.name?.trim() === ""
      )
        ? []
        : memberData?.coaches
          .filter((item) => item.id !== 0 && item.name?.trim() !== "") // Filter out invalid entries
          .map((item) => item.id);

      if (
        memberpayload?.mobile_number &&
        [0, 2, 3, 4].includes(memberpayload.mobile_number?.length)
      ) {
        memberpayload.mobile_number = `+92`;
      }

      reset(memberpayload);
      if (memberData?.nic?.length && !memberData.is_business) {
        setValue("nic", formatNIC(memberData?.nic) ?? "");
      }
      // setAvatar(memberpayload.profile_img as string);
    } else {
      const total = memberCountData?.total_members as number;
      if (total >= 0 && action == "add") {
        initialValues.own_member_id = `${orgName?.slice(0, 2)}-${total + 1}`;
      }
      reset(initialValues);
    }
  }, [open, action]);

  useEffect(() => {
    if (!open) return;
    const total = memberCountData?.total_members as number;
    if (total >= 0 && action === "add") {
      form.setValue(
        "own_member_id",
        `${other_settings?.member_id_prefix}${total + 1}`
      );
      // form.setValue("own_member_id", `${orgName?.slice(0, 2)}-${total + 1}`);
      form.clearErrors();
    }
  }, [open, memberCountData]);

  const handleSearchChange = (term: string) => {
    setSearchTerm(term);
  };

  const handleUserSelect = (selectedUser: User | null) => {
    console.log("Selected User:", selectedUser);

    if (!selectedUser) return; // Guard against null input

    const modifiedUser = {
      ...selectedUser,
      own_member_id: watcher.own_member_id,
      is_business: false,
      business_id: null,
      client_status: "pending",
      mobile_number:
        !selectedUser.mobile_number ||
          [0, 2, 3, 4].includes(selectedUser.mobile_number?.length)
          ? "+92"
          : selectedUser.mobile_number,
    };

    reset(modifiedUser);
  };

  const handleEditGroup = (group: DropdownOption) => {
    setOpenGroupForm(true);
    setGroup(group);
  };

  function handleClose() {
    setAvatar(null);
    setSelectedImage(null);
    clearErrors();
    reset(initialValues, {
      keepIsSubmitted: false,
      keepSubmitCount: false,
      keepDefaultValues: true,
    });
    setMemberData(null);
    setOpen(false);
    setIsDialogOpen(false);
    setAction("add");
    setMembershipPlansdata([
      {
        id: 1,
        item_id: 0,
        item_type: ProductType.Membership,
        description: "",
        income_category_id: 0,
        quantity: 0,
        sale_tax_id: 0,
        net_price: 0,
        price: 0,
        tax_rate: 0,
        discount: 0,
        sub_total: 0,
        tax_perc: 0,
        revenue_type: "income",
        is_srb: false,
        tax_type: "",
        tax_name: "",
        total: 0,
        tax_amount: 0,
        booking_date: "",
      },
    ]);
  }

  async function onSubmit(data: MemberInputTypes) {
    let updatedData = {
      ...data,
      org_id: orgId,
      first_name: data.first_name?.trim(),
      last_name: data.last_name?.trim(),
      dob: format(new Date(data.dob!), "yyyy-MM-dd"),
      business_id: data.is_business ? null : data.business_id,
      nic: data.nic?.length ? data.nic?.replace(/-/g, "") : data.nic?.length && data.is_business ? data.nic : undefined,
    };

    if (selectedImage) {
      try {
        if (updatedData.profile_img !== "" && selectedImage) {
          await deleteCognitoImage(updatedData.profile_img as string);
        }
        const getUrl = await UploadCognitoImage(selectedImage);
        updatedData = {
          ...updatedData,
          profile_img: getUrl?.location as string,
        };
      } catch (error) {
        console.error("Upload failed:", error);
        toast({
          variant: "destructive",
          title: "Image Upload Failed",
          description: "Please try again.",
        });
        return;
      }
    }
    try {
      let resp;
      if (action == "add") {
        const validMembershipPlans = membershipPlansdata.filter(
          (plan) =>
            plan.item_id !== undefined &&
            plan.item_id !== null &&
            plan.item_id !== 0
        );
        console.log("validMembershipPlans", validMembershipPlans);
        const payload = {
          ...updatedData,
        };

        if (payload.is_business) {
          payload.business_id = null;
        }

        resp = await addMember(payload).unwrap();
        if (resp) {
          toast({
            variant: "success",
            title: "Member Created Successfully ",
          });
          refetch?.();
          refecthCount();
          refetchBusiness();
        }
      } else {
        const validMembershipPlans = membershipPlansdata.filter(
          (plan) =>
            plan.item_id !== undefined &&
            plan.item_id !== null &&
            plan.item_id !== 0
        );
        console.log("validMembershipPlans", validMembershipPlans);

        const payload = {
          ...updatedData,
        };

        if (payload.is_business) {
          payload.business_id = null;
        }

        resp = await editMember({
          ...payload,
          id: memberData?.id as number,
        }).unwrap();
        console.log("Payload", payload);
        if (resp) {
          toast({
            variant: "success",
            title: "Member Updated Successfully ",
          });
          refetch?.();
          refetchBusiness();
        }
      }

      if (isNewMembershipAdded()) {
        setAction("member-created");
        const invoicePayload = {
          org_id: orgId,
          invoice_number: "",
          member_id: action == "add" ? resp.id : memberData?.id,
          member_name: data.first_name + " " + data.last_name,
          member_email: data.email as string,
          member_address: data.address_1,
          member_nic: data.nic,
          member_gender: data.gender,
          is_business: data.is_business,
          notes: "",
          total: 0,
          sub_total: 0,
          tax_amount: 0,
          discount: 0,
          status: InvoiceStatus.Pending,
          invoice_type: InvoiceType.Proforma,
          payment_date: new Date().toISOString(),
          id: 0,
          total_quantity: 0,
          items: membershipPlansdata,
          seller_id: userInfo?.id,
          seller_name: userInfo?.first_name,
        };

        setEditInvoiceData(invoicePayload as InvoicePayload);
        setIsDialogOpen(true);
      } else {
        handleClose();
      }
    } catch (error: unknown) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description:
            typedError.data?.detail ??
            (typedError.data as { message?: string }).message,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
      refecthCount();
    }
  }
  const onError = () => {
    toast({
      variant: "destructive",
      description: "Please fill all the mandatory fields",
    });
  };
  const isNewMembershipAdded = () => {
    return membershipPlansdata.some(
      (plan) =>
        plan.item_id !== undefined &&
        plan.item_id !== null &&
        plan.item_id !== 0
    );
  };

  console.log(
    { watcher, errors, action, isSubmitting, membershipPlansdata },
    "memberform"
  );

  return (
    <Sheet open={open} onOpenChange={handleClose}>
      <SheetContent
        hideCloseButton
        className="w-[85%] mdx:w-[75%] !max-w-[1120px] py-0 custom-scrollbar"
      >
        <FormProvider {...form}>
          <form
            key={action}
            noValidate
            onSubmit={handleSubmit(onSubmit, onError)}
          >
            <SheetHeader className="sticky top-0 z-40 py-4 bg-white">
              <SheetTitle>
                <div className="flex justify-between gap-5 items-start  bg-white">
                  <div>
                    <p className="font-semibold">
                      {action == "add" ? "Add" : "Edit"} Member
                    </p>
                    <div className="text-sm">
                      <span className="text-gray-400 pr-1 font-semibold capitalize">
                        {breadcrumb}
                      </span>{" "}
                      <span className="text-gray-400 font-semibold">/</span>
                      <span className="pl-1 text-primary font-semibold ">
                        {action == "add" ? "Add" : "Edit"} Member
                      </span>
                    </div>
                  </div>

                  <div className="flex gap-2">
                    <div>
                      <Button
                        type={"button"}
                        onClick={handleClose}
                        className="
                        gap-2 bg-transparent border border-primary text-black hover:border-primary hover:bg-muted"
                      >
                        <RxCross2 className="w-4 h-4" /> Cancel
                      </Button>
                    </div>
                    <div>
                      <LoadingButton
                        type="submit"
                        className="text-nowrap  bg-primary text-black text-center items-center gap-2"
                        loading={isSubmitting}
                        disabled={isSubmitting}
                      >
                        {!isSubmitting && (
                          <i className="fa-regular fa-floppy-disk text-base px-1 "></i>
                        )}
                        {(() => {
                          if (action === "add") {
                            return isNewMembershipAdded()
                              ? "Create Invoice"
                              : "Save";
                          } else {
                            return isNewMembershipAdded()
                              ? "Create Invoice"
                              : "Update";
                          }
                        })()}
                      </LoadingButton>
                    </div>
                  </div>
                </div>
              </SheetTitle>
              <Separator className=" h-[1px] rounded-full my-2" />
            </SheetHeader>
            <SheetDescription className="pb-4">
              <div className="flex justify-between items-center sticky top-0 z-20 bg-white w-full">
                <div className="flex flex-row gap-4 items-center ">
                  <div className="relative flex">
                    <img
                      id="avatar"
                      src={
                        avatar
                          ? String(avatar)
                          : watcher.profile_img
                            ? watcher.profile_img.includes('https://')
                              ? watcher.profile_img
                              : `${VITE_VIEW_S3_URL}/${watcher.profile_img}`
                            : profileimg
                      }
                      alt={profileimg}
                      className="w-14 h-14 rounded-full object-cover mb-4 relative"
                    />
                  </div>
                  <input
                    type="file"
                    id="fileInput"
                    className="hidden"
                    onChange={handleImageChange}
                  />
                  <Button
                    onClick={handleSetAvatarClick}
                    variant={"outline"}
                    type="button"
                    className="px-4 py-2 bg-transparent gap-2 text-black rounded hover:bg-primary hover:text-white"
                  >
                    <Webcam className="h-4 w-4" />
                    Profile Snapshot
                  </Button>
                </div>
                <div>
                  <SearchComboBox
                    items={userOptions || []}
                    onSearchChange={handleSearchChange}
                    onItemSelect={handleUserSelect}
                    placeholder="Start typing to search users..."
                    className="min-w-[400px]"
                    loading={loading}
                    getItemKey={(user) => user.id}
                    capitalize={true}
                    getItemLabel={(user) =>
                      `${user.first_name} ${user.last_name}`
                    }
                    renderItem={(user) => (
                      <div className="space-y-1">
                        <div className="flex gap-2 items-center">
                          <span className="capitalize">{`${user.first_name} ${user.last_name}`}</span>
                        </div>
                        <div className="text-muted-foreground text-xs">
                          {user.email}
                        </div>
                      </div>
                    )}
                    emptyMessage="No users found matching your search"
                    debounceMs={500}
                  />
                </div>
              </div>

              <div className="space-y-6">
                <div className="space-y-2">
                  <h1 className="font-semibold text-lg mb-4 text-gray-900">
                    Basic Information
                  </h1>

                  <div className="w-full grid grid-cols-3 gap-3 justify-between items-center">
                    <div className="relative">
                      <FloatingLabelInput
                        id="own_member_id"
                        label="Member Id"
                        text="*"
                        className="disabled:!opacity-100 disabled:text-gray-800 placeholder:text-gray-800"
                        // disabled
                        required={true}
                        min={1}
                        max={12}
                        {...register("own_member_id", {
                          required: "Required",
                          minLength: {
                            value: 1,
                            message: "Required",
                          },
                          maxLength: {
                            value: 12,
                            message: "Cannot be greater then 12 characters",
                          },
                        })}
                        error={
                          errors?.own_member_id
                            ?.message as keyof MemberInputTypes
                        }
                        onChange={(e) => {
                          // Prevent leading and trailing spaces in real time
                          const trimmedValue = e.target.value.replace(
                            /^\s+|\s+$/g,
                            ""
                          );
                          setValue("own_member_id", trimmedValue); // Update field value
                        }}
                      />
                    </div>
                    <div className="relative ">
                      {action == "add" ||
                        (action == "edit" &&
                          watcher.client_status == "pending") ? (
                        <FloatingLabelInput
                          id="email"
                          className=""
                          type="email"
                          label="Email Address"
                          text="*"
                          {...register("email", {
                            required: "Required",
                            setValueAs: (value) => value.toLowerCase(),
                            maxLength: {
                              value: 50,
                              message: "Should be 50 characters or less",
                            },
                            pattern: {
                              value:
                                /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
                              message: "Incorrect email format",
                            },
                          })}
                          error={errors.email?.message}
                        />
                      ) : (
                        <TooltipProvider>
                          <Tooltip>
                            <TooltipTrigger asChild>
                              <FloatingLabelInput
                                id="email"
                                className=""
                                type="email"
                                label="Email Address"
                                text="*"
                                disabled={
                                  action == "edit" &&
                                  watcher.client_status != "pending"
                                }
                                {...register("email", {
                                  required: "Required",
                                  maxLength: {
                                    value: 50,
                                    message: "Should be 50 characters or less",
                                  },
                                  pattern: {
                                    value:
                                      /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/i,
                                    message: "Incorrect email format",
                                  },
                                })}
                                error={errors.email?.message}
                              />
                            </TooltipTrigger>

                            <TooltipContent>
                              You cannot update the email address once the
                              member is active
                            </TooltipContent>
                          </Tooltip>
                        </TooltipProvider>
                      )}
                    </div>
                    <div className="relative ">
                      <FloatingLabelInput
                        id="first_name"
                        label="First Name"
                        text="*"
                        {...register("first_name", {
                          required: "Required",
                          setValueAs: (value) => value.toLowerCase(),
                          validate: (value) => {
                            if (value?.trim() === "") {
                              return "Required";
                            }
                            return true;
                          },
                          maxLength: {
                            value: 40,
                            message: "Should be 40 characters or less",
                          },
                          pattern: {
                            value: /^[A-Za-z\-\s]+$/, // Adjust this regex based on your needs
                            message:
                              "Only alphabets, hyphen (-) and spaces are allowed",
                          },
                        })}
                        error={
                          errors?.first_name?.message as keyof MemberInputTypes
                        }
                        className="capitalize"
                      />
                    </div>
                    <div className="relative ">
                      <FloatingLabelInput
                        id="last_name"
                        label="Last Name"
                        text="*"
                        {...register("last_name", {
                          required: "Required",
                          setValueAs: (value) => value.toLowerCase(),
                          validate: (value) => {
                            if (value?.trim() === "") {
                              return "Required";
                            }
                            return true;
                          },
                          maxLength: {
                            value: 40,
                            message: "Should be 40 characters or less",
                          },
                          pattern: {
                            value: /^[A-Za-z\-\s]+$/, // Adjust this regex based on your needs
                            message:
                              "Only alphabets, hyphen (-) and spaces are allowed",
                          },
                        })}
                        error={
                          errors?.last_name?.message as keyof MemberInputTypes
                        }
                        className="capitalize"
                      />
                    </div>
                    <div className="relative ">
                      <Controller
                        name={"gender" as keyof MemberInputTypes}
                        rules={{ required: "Required" }}
                        control={control}
                        render={({
                          field: { onChange, value, onBlur },
                          fieldState: { invalid, error },
                        }) => (
                          <Select
                            onValueChange={(value: Gender) =>
                              setValue("gender", value)
                            }
                            value={value as Gender}
                          >
                            <SelectTrigger
                              floatingLabel="Gender"
                              text="*"
                              className={`text-black`}
                            >
                              <SelectValue placeholder="Select Gender" />
                            </SelectTrigger>
                            <SelectContent className="">
                              <SelectItem value="male">Male</SelectItem>
                              <SelectItem value="female">Female</SelectItem>
                              <SelectItem value="other">Other</SelectItem>
                              <SelectItem value="prefer not to say">
                                Prefer not to say
                              </SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.gender?.message && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {errors.gender?.message}
                        </span>
                      )}
                    </div>
                    <div className="relative ">
                      <Controller
                        name={"dob" as keyof MemberInputTypes}
                        rules={{ required: "Required" }}
                        control={control}
                        render={({
                          field: { onChange, value, onBlur },
                          fieldState: { invalid, error },
                        }) => (
                          <Popover open={dob} onOpenChange={setDob}>
                            <PopoverTrigger asChild>
                              <FormControl>
                                <div className="relative">
                                  <span className="absolute p-0 text-xs text-black left-2 -top-1.5 px-1 bg-white">
                                    Date of brith{""}
                                    <span className="text-red-500">*</span>
                                  </span>
                                  <Button
                                    variant={"outline"}
                                    type="button"
                                    className={cn(
                                      "w-full pl-3 text-gray-800 text-left font-normal hover:bg-transparent border-[1px]"
                                    )}
                                  >
                                    {(value as Date) ? (
                                      format(value as Date, "dd-MM-yyyy")
                                    ) : (
                                      <span>Select date of birth</span>
                                    )}
                                    <CalendarIcon className="ml-auto h-4 w-4 opacity-50" />
                                  </Button>
                                  {errors.dob?.message && (
                                    <span className="text-red-500 text-xs mt-[5px]">
                                      {errors.dob?.message}
                                    </span>
                                  )}
                                </div>
                              </FormControl>
                            </PopoverTrigger>
                            <PopoverContent
                              className="w-auto p-0"
                              align="center"
                            >
                              <Calendar
                                mode="single"
                                captionLayout="dropdown-buttons"
                                selected={value as Date}
                                defaultMonth={
                                  (value as Date) ? (value as Date) : undefined
                                }
                                onSelect={(selectedDate) => {
                                  if (selectedDate) {
                                    const today = new Date();
                                    const oneYearAgo = new Date();
                                    oneYearAgo.setFullYear(
                                      today.getFullYear() - 1
                                    );

                                    // Check if the selected date is at least 1 year old
                                    if (selectedDate <= oneYearAgo) {
                                      onChange(selectedDate);
                                      setDob(false);
                                    } else {
                                      toast({
                                        variant: "destructive",
                                        description:
                                          "Date of birth must be at least 1 year old.",
                                      });
                                    }
                                  }
                                }}
                                fromYear={1950}
                                toYear={new Date().getFullYear()}
                                disabled={(date: any) =>
                                  date > new Date() ||
                                  date < new Date("1950-01-01")
                                }
                                initialFocus
                              />
                            </PopoverContent>
                          </Popover>
                        )}
                      />
                    </div>

                    <div className="relative ">
                      <FloatingLabelInput
                        type="tel"
                        id="phone"
                        label="Landline Number"
                        className=""
                        {...register("phone", {
                          pattern: {
                            value: /^\d{1,15}$/,
                            message: "Must be a number between 1 and 15 digits",
                          },
                        })}
                        error={errors.phone?.message}
                      />
                    </div>
                    <div className="relative ">
                      <Controller
                        name="mobile_number"
                        control={control}
                        defaultValue=""
                        rules={{ validate: validatePhone }}
                        render={({ field: { onChange, value } }) => (
                          <div className="relative ">
                            <span className="absolute p-0 text-xs left-12 -top-2 px-1 bg-white z-10">
                              Phone Number
                            </span>
                            <PhoneInput
                              defaultCountry="pk"
                              value={value}
                              onChange={onChange}
                              // forceDialCode={true}
                              inputClassName="w-full"
                            />
                          </div>
                        )}
                      />
                      {errors.mobile_number && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {errors.mobile_number.message}
                        </span>
                      )}
                    </div>

                    <div className="relative">
                      <Controller
                        name={"nic" as keyof MemberInputTypes}
                        control={control}
                        rules={{
                          pattern: watcher.is_business ? undefined : {
                            value: /^\d{5}-\d{7}-\d$/,
                            message: "CNIC must follow #####-#######-#",
                          },
                        }}
                        render={({ field: { onChange, value } }) => (
                          <FloatingLabelInput
                            label={watcher.is_business ? "NTN" : "CNIC"}
                            type="text"
                            value={value ? String(value) : ""} // Ensure value is a string or empty
                            onChange={(e) => {
                              if (watcher.is_business) {
                                onChange(e.target.value);
                              } else {
                                const formattedValue = formatNIC(e.target.value);
                                onChange(formattedValue ?? ""); // Default to empty string
                              }
                            }}
                          />
                        )}
                      />
                      {errors.nic?.message && (
                        <span className="text-red-500 text-xs pt-2">
                          {errors.nic?.message}
                        </span>
                      )}
                    </div>
                    <div className="relative ">
                      <Controller
                        name={"client_status" as keyof MemberInputTypes}
                        rules={{ required: "Required" }}
                        control={control}
                        render={({
                          field: { onChange, value, onBlur },
                          fieldState: { invalid, error },
                        }) => (
                          <Select
                            onValueChange={(value: statusEnum) =>
                              setValue("client_status", value)
                            }
                            value={value as statusEnum}
                            disabled={value === "pending"}
                          >
                            <SelectTrigger
                              floatingLabel="Status"
                              text="*"
                              className={`text-black`}
                            >
                              <SelectValue placeholder="Select Status" />
                            </SelectTrigger>
                            <SelectContent className="">
                              <SelectItem
                                value="pending"
                                className={`${action == "edit" && "hidden"}`}
                              >
                                Pending
                              </SelectItem>
                              <SelectItem value="active">Active</SelectItem>
                              <SelectItem value="inactive">Inactive</SelectItem>
                            </SelectContent>
                          </Select>
                        )}
                      />
                      {errors.client_status?.message && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {errors.client_status?.message}
                        </span>
                      )}
                    </div>

                    <div className="relative flex gap-2 item-center">
                      <div className="w-full">
                        <Controller
                          name={"member_group_id" as keyof MemberInputTypes}
                          // rules={{ required: "Required" }}
                          control={control}
                          render={({
                            field: { onChange, value, onBlur },
                            fieldState: { invalid, error },
                          }) => (
                            <div className="relative">
                              <label
                                htmlFor="member_group_id"
                                className={`absolute left-3 top-0.5 bg-textwhite transform -translate-y-1/2 pointer-events-none transition-all duration-200 ${value
                                  ? "text-xs -top-2.5"
                                  : "text-xs text-black"
                                  }`}
                              >
                                Group
                              </label>
                              <Popover
                                open={openGroup}
                                onOpenChange={setOpenGroup}
                              >
                                <PopoverTrigger asChild>
                                  <FormControl>
                                    <Button
                                      variant="outline"
                                      role="combobox"
                                      className="capitalize w-full hover:bg-transparent border-[1px] shadow-sm"
                                    >
                                      <span className="w-full text-left font-normal text-black">
                                        {value
                                          ? memberGroupOptions?.find(
                                            (group) => group.value === value
                                          )?.label
                                          : "Select Group"}
                                      </span>
                                      <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                    </Button>
                                  </FormControl>
                                </PopoverTrigger>
                                <PopoverContent
                                  className="p-0 relative "
                                  side="bottom"
                                >
                                  <Command className="w-full ">
                                    <CommandList>
                                      <CommandInput placeholder="Select Source" />
                                      <CommandEmpty>
                                        No group found.
                                      </CommandEmpty>
                                      <CommandGroup className="">
                                        {memberGroupOptions?.map((group, i) => (
                                          <CommandItem
                                            key={i}
                                            value={group.label}
                                            className="capitalize"
                                            onSelect={() => {
                                              setValue(
                                                "member_group_id",
                                                Number(group.value)
                                              );
                                              setOpenGroup(false);
                                            }}
                                          >
                                            <Check
                                              className={cn(
                                                "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                                group.label == value
                                                  ? "opacity-100"
                                                  : "opacity-0"
                                              )}
                                            />
                                            {group.label}
                                          </CommandItem>
                                        ))}
                                      </CommandGroup>
                                    </CommandList>
                                  </Command>
                                </PopoverContent>
                              </Popover>
                            </div>
                          )}
                        />
                        {errors.source_id?.message && (
                          <span className="text-red-500 text-xs mt-[5px]">
                            {errors.source_id?.message}
                          </span>
                        )}
                      </div>
                      <Button
                        onClick={() => setOpenGroupForm(true)}
                        title="Add New Group"
                        className="p-2"
                      >
                        <Plus />
                      </Button>
                    </div>
                    <div className="relative ">
                      <Controller
                        name={"source_id" as keyof MemberInputTypes}
                        rules={{ required: "Required" }}
                        control={control}
                        render={({
                          field: { onChange, value, onBlur },
                          fieldState: { invalid, error },
                        }) => (
                          <div className="relative">
                            <label
                              htmlFor="source_id"
                              className={`absolute left-3 top-0.5 bg-textwhite transform -translate-y-1/2 pointer-events-none transition-all duration-200 ${value
                                ? "text-xs -top-2.5"
                                : "text-xs text-black"
                                }`}
                            >
                              Source <span className="text-red-500">*</span>
                            </label>
                            <Popover
                              open={openSource}
                              onOpenChange={setOpenSource}
                            >
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    className="capitalize w-full hover:bg-transparent border-[1px] shadow-sm"
                                  >
                                    <span className="w-full text-left font-normal text-black">
                                      {value
                                        ? sources?.find(
                                          (source) => source.id === value
                                        )?.source
                                        : "Select Source"}
                                    </span>
                                    <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent
                                className="p-0 relative "
                                side="bottom"
                              >
                                <Command className="w-full ">
                                  <CommandList>
                                    <CommandInput placeholder="Select Source" />
                                    <CommandEmpty>
                                      No Source List found.
                                    </CommandEmpty>
                                    <CommandGroup className="">
                                      {sources?.map((sources: SourceTypes) => (
                                        <CommandItem
                                          value={sources.source}
                                          key={sources.id}
                                          className="capitalize"
                                          onSelect={() => {
                                            setValue("source_id", sources.id);
                                            setOpenSource(false);
                                          }}
                                        >
                                          <Check
                                            className={cn(
                                              "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                              sources.id == value
                                                ? "opacity-100"
                                                : "opacity-0"
                                            )}
                                          />
                                          {sources.source}
                                        </CommandItem>
                                      ))}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                        )}
                      />
                      {errors.source_id?.message && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {errors.source_id?.message}
                        </span>
                      )}
                    </div>

                    <div className="relative ">
                      <Controller
                        name={"coach_ids" as keyof MemberInputTypes}
                        // rules={{ required: "Required" }}
                        control={control}
                        render={({
                          field: { onChange, value, onBlur },
                          fieldState: { invalid, error },
                        }) => (
                          <MultiSelect
                            floatingLabel={"Assign Coaches"}
                            options={coachOptions}
                            defaultValue={watch("coach_ids") || []} // Ensure defaultValue is always an array
                            onValueChange={(selectedValues) => {
                              console.log("Selected Values: ", selectedValues); // Debugging step
                              onChange(selectedValues); // Pass selected values to state handler
                            }}
                            placeholder={"Select coaches"}
                            variant="inverted"
                            maxCount={1}
                            className="focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 "
                          />
                        )}
                      />
                      {errors.coach_ids?.message && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {errors.coach_ids?.message}
                        </span>
                      )}
                    </div>
                    <div className="relative ">
                      <div className="justify-start items-center flex">
                        <Controller
                          name={"is_business" as keyof MemberInputTypes}
                          control={control}
                          render={({
                            field: { onChange, value, onBlur },
                            fieldState: { invalid, error },
                          }) => (
                            <div className="flex flex-row gap-3 items-center justify-between ">
                              <div className="space-y-0.5">
                                <FormLabel className="text-base">
                                  Business :
                                </FormLabel>
                              </div>
                              <FormControl>
                                <Switch
                                  checked={value as boolean}
                                  onCheckedChange={onChange}
                                />
                              </FormControl>
                            </div>
                          )}
                        />
                        {errors.is_business?.message && (
                          <span className="text-red-500 text-xs mt-[5px]">
                            {errors.is_business?.message}
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      className={`relative  ${watcher.is_business ? "hidden" : ""
                        }`}
                    >
                      <Controller
                        name={"business_id" as keyof MemberInputTypes}
                        // rules={{ required: !watcher.is_business && "Required" }}
                        control={control}
                        render={({
                          field: { onChange, value, onBlur },
                          fieldState: { invalid, error },
                        }) => (
                          <div className="relative">
                            <label
                              htmlFor="business_id"
                              className={`absolute left-3 top-0.5 bg-textwhite transform -translate-y-1/2 pointer-events-none transition-all duration-200 ${value
                                ? "text-xs -top-2.5"
                                : "text-xs text-black"
                                }`}
                            >
                              Business
                            </label>
                            <Popover
                              open={openBusiness}
                              onOpenChange={setOpenBusiness}
                            >
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    className="capitalize w-full hover:bg-transparent border-[1px] shadow-sm"
                                  >
                                    <span className="w-full text-left font-normal text-black">
                                      {value
                                        ? business?.find(
                                          (business) => business.id === value
                                        )?.full_name
                                        : "Select Business"}
                                    </span>
                                    <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent
                                className="p-0 relative "
                                side="bottom"
                              >
                                <Command className="w-full ">
                                  <CommandList>
                                    <CommandInput placeholder="Select Business" />
                                    <CommandEmpty>
                                      No Business found.
                                    </CommandEmpty>
                                    <CommandGroup className="">
                                      {business?.map(
                                        (business: BusinessTypes) => (
                                          <CommandItem
                                            value={business.full_name}
                                            key={business.id}
                                            className="capitalize"
                                            onSelect={() => {
                                              if (value === business.id) {
                                                setValue("business_id", null);
                                              } else {
                                                setValue(
                                                  "business_id",
                                                  business.id
                                                );
                                              }
                                              setOpenBusiness(false);
                                            }}
                                          >
                                            <Check
                                              className={cn(
                                                "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                                business.id == value
                                                  ? "opacity-100"
                                                  : "opacity-0"
                                              )}
                                            />
                                            {business.full_name}
                                          </CommandItem>
                                        )
                                      )}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                        )}
                      />
                      {errors.business_id?.message && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {errors.business_id?.message}
                        </span>
                      )}
                    </div>
                    <div className="relative row-span-2">
                      <FloatingLabelInput
                        id="notes"
                        label="Notes"
                        rows={2}
                        maxLength={200}
                        type="textarea"
                        {...register("notes", {
                          maxLength: {
                            value: 200,
                            message: "Notes should not exceed 200 characters",
                          },
                          pattern: {
                            value: /^[A-Za-z0-9\.\,\-\_\s]+$/,
                            message:
                              "Allowed: A-Z, a-z, 0-9, period (.), comma (,), hyphen (-), underscore (_), space ( )",
                          },
                        })}
                        error={errors.notes?.message}
                      />
                    </div>
                  </div>
                </div>

                <div className="space-y-2">
                  <h1 className="font-semibold text-lg my-4 text-gray-900">
                    Address
                  </h1>
                  <div className="w-full grid grid-cols-3 gap-3 justify-between items-center">
                    <div className="relative ">
                      <FloatingLabelInput
                        id="address_1"
                        label="Street Address"
                        {...register("address_1", {
                          maxLength: {
                            value: 50,
                            message:
                              "Address should be less than 50 characters",
                          },
                          pattern: {
                            value: /^[A-Za-z0-9\.\,\-\_\s]+$/,
                            message:
                              "Allowed: A-Z, a-z, 0-9, period (.), comma (,), hyphen (-), underscore (_), space ( )",
                          },
                        })}
                        error={errors.address_1?.message}
                      />
                    </div>
                    <div className="relative ">
                      <FloatingLabelInput
                        id="address_2"
                        label="Extra Address"
                        {...register("address_2", {
                          maxLength: {
                            value: 50,
                            message:
                              "Address should be less than 50 characters",
                          },
                          pattern: {
                            value: /^[A-Za-z0-9\.\,\-\_\s]+$/,
                            message:
                              "Allowed: A-Z, a-z, 0-9, period (.), comma (,), hyphen (-), underscore (_), space ( )",
                          },
                        })}
                        error={errors.address_2?.message}
                      />
                    </div>
                    <div className="relative ">
                      <FloatingLabelInput
                        id="zipcode"
                        label="Zip Code"
                        {...register("zipcode", {
                          maxLength: {
                            value: 15,
                            message:
                              "Zip code should be less than 15 characters",
                          },
                          pattern: {
                            value: /^[\d]+$/i,
                            message: "Allowed: numbers only (0-9)",
                          },
                        })}
                        error={errors.zipcode?.message}
                      />
                    </div>
                    <div className="relative">
                      <Controller
                        name={"country_id" as keyof MemberInputTypes}
                        rules={{ required: "Required" }}
                        control={control}
                        render={({
                          field: { onChange, value, onBlur },
                          fieldState: { invalid, error },
                        }) => (
                          <div className="flex flex-col w-full relative">
                            <label
                              htmlFor="country_id"
                              className={`absolute left-3 top-0.5 bg-textwhite transform -translate-y-1/2 pointer-events-none transition-all duration-200 ${value
                                ? "text-xs -top-2.5"
                                : "text-xs text-black"
                                }`}
                            >
                              Country <span className="text-red-500">*</span>
                            </label>
                            <Popover open={country} onOpenChange={setCountry}>
                              <PopoverTrigger asChild>
                                <FormControl>
                                  <Button
                                    variant="outline"
                                    role="combobox"
                                    className={cn(
                                      "font-normal text-gray-800 border-[1px] justify-between hover:bg-transparent hover:text-gray-800",
                                      !value && "  focus:border-primary "
                                    )}
                                  >
                                    {value
                                      ? countries?.find(
                                        (country: CountryTypes) =>
                                          country.id === value // Compare with numeric value
                                      )?.country // Display country name if selected
                                      : "Select country"}
                                    <ChevronDownIcon className="ml-2 h-4 w-4 shrink-0 opacity-50" />
                                  </Button>
                                </FormControl>
                              </PopoverTrigger>
                              <PopoverContent className="p-0  ">
                                <Command>
                                  <CommandList>
                                    <CommandInput placeholder="Select Country" />
                                    <CommandEmpty>
                                      No country found.
                                    </CommandEmpty>
                                    <CommandGroup className="">
                                      {countries?.map(
                                        (country: CountryTypes) => (
                                          <CommandItem
                                            value={country.country}
                                            key={country.id}
                                            onSelect={() => {
                                              setValue(
                                                "country_id",
                                                country.id // Set country_id to country.id as number
                                              );
                                              setCountry(false);
                                            }}
                                          >
                                            <Check
                                              className={cn(
                                                "mr-2 h-4 w-4 rounded-full border-2 border-green-500",
                                                country.id == value
                                                  ? "opacity-100"
                                                  : "opacity-0"
                                              )}
                                            />
                                            {country.country}
                                          </CommandItem>
                                        )
                                      )}
                                    </CommandGroup>
                                  </CommandList>
                                </Command>
                              </PopoverContent>
                            </Popover>
                          </div>
                        )}
                      />
                      {errors.country_id?.message && (
                        <span className="text-red-500 text-xs mt-[5px]">
                          {errors.country_id?.message}
                        </span>
                      )}
                    </div>
                    <div className="relative ">
                      <FloatingLabelInput
                        id="city"
                        label="City"
                        {...register("city", {
                          maxLength: {
                            value: 50,
                            message: "City should be less than 50 characters",
                          },
                          pattern: {
                            value: /^[A-Za-z0-9 -]+$/i,
                            message:
                              "Allowed: A-Z, a-z, 0-9, hyphen (-), space ( )",
                          },
                        })}
                        error={errors?.city?.message as keyof MemberInputTypes}
                      />
                    </div>
                  </div>
                </div>

                {memberData?.membership_plans && (
                  <div className="space-y-2">
                    <h1 className="font-semibold text-lg mt-4 text-gray-900">
                      Current memberships
                    </h1>

                    <div className="flex flex-wrap gap-3">
                      {memberData.membership_plans.length > 0 ? memberData.membership_plans.map((plan) => (
                        <Badge className="w-fit bg-green-300">
                          <span className="font-medium text-gray-700 normal-case">
                            {plan.membership_plan_name}
                          </span>
                        </Badge>
                      )) : <>
                        <p className="pl-4">No memberships assigned...</p>
                      </>}
                    </div>
                  </div>
                )}

                <div className="space-y-2">
                  <h1 className="font-semibold text-lg mt-4 text-gray-900">
                    Select memberships
                  </h1>

                  <div className="flex gap-2 flex-col">
                    {membershipPlansdata?.map(
                      (plan: BaseItem, index: number) => (
                        <>
                          {membershipPlansdata?.length > 1 && index > 0 && (
                            <div className="pt-2">
                              <Separator />
                            </div>
                          )}

                          <div
                            className="grid grid-cols-12 gap-3"
                            key={`plan-${plan.id}-${index}`} // Use more stable key that includes plan.id
                          >
                            <div className="relative col-span-4">
                              <Combobox
                                defaultValue={plan.description || ""} // Use description since we match by name
                                setFilter={(value: string) =>
                                  handleMembershipPlanChange(
                                    index,
                                    "membership_plan_id",
                                    value
                                  )
                                }
                                list={membershipPlansOptions}
                                label={"Membership"}
                              />
                            </div>

                            {membershipPlansdata?.length > 0 && (
                              <Button
                                type="button"
                                className="text-red-500 px-2 disabled:cursor-none"
                                variant={"ghost"}
                                disabled={
                                  membershipPlansdata.length === 0 ||
                                  membershipPlansdata.some(
                                    (plan) =>
                                      plan.item_id === undefined ||
                                      plan.item_id === null ||
                                      plan.item_id === 0
                                  )
                                }
                                onClick={() => handleRemovePlan(index)}
                              >
                                <TooltipProvider>
                                  <Tooltip>
                                    <TooltipTrigger asChild>
                                      <i className="fa-solid fa-trash"></i>
                                    </TooltipTrigger>
                                    <TooltipContent>
                                      Delete Membership plan
                                    </TooltipContent>
                                  </Tooltip>
                                </TooltipProvider>
                              </Button>
                            )}
                          </div>
                        </>
                      )
                    )}

                    <div>
                      <Button
                        type="button"
                        onClick={handleAddPlan}
                        variant={"ghost"}
                        disabled={
                          membershipPlansdata.length === 0 ||
                          membershipPlansdata.some(
                            (plan) =>
                              plan.item_id === undefined ||
                              plan.item_id === null
                          )
                        }
                        className="px-2 font-medium text-primary !hover:bg-none !hover:text-primary"
                      >
                        + Assign more Membership
                      </Button>
                    </div>
                  </div>
                </div>
              </div>

              <InvoiceForm
                isOpen={isDialogOpen}
                setOpen={setIsDialogOpen}
                action={action}
                setAction={setAction}
                refetch={refetch}
                editModeData={editInvoiceData}
              />

              <MemberGroupForm
                isOpen={openGroupForm}
                setOpen={setOpenGroupForm}
                refetch={refetchGroups}
                group={group}
                setGroup={setGroup}
              />

              <ConfirmationDialog
                isOpen={isDeleteGroup}
                onClose={onClose}
                onConfirm={onConfirm}
                title={"Delete Group"}
              />
            </SheetDescription>
          </form>
        </FormProvider>
      </SheetContent>
    </Sheet>
  );
};

export default MemberForm;

interface MemberGroupFormProps {
  isOpen: boolean;
  setOpen: React.Dispatch<React.SetStateAction<boolean>>;
  refetch: () => void;
  group: DropdownOption | undefined;
  setGroup: React.Dispatch<React.SetStateAction<DropdownOption | undefined>>;
}

const MemberGroupForm = ({
  isOpen,
  setOpen,
  refetch,
  group,
  setGroup,
}: MemberGroupFormProps) => {
  const [createMemberGroup] = useCreateMemberGroupMutation();
  const [updateMemberGroup] = useUpdateMemberGroupMutation();

  const initialValues = {
    name: "",
  };

  const form = useForm<MemberGroup>({
    mode: "all",
    defaultValues: initialValues,
  });
  const {
    control,
    register,
    handleSubmit,
    clearErrors,
    reset,
    formState: { errors, isSubmitting },
  } = form;

  useEffect(() => {
    if (group?.value) {
      const payload = {
        id: Number(group.value),
        name: group.label,
      };
      reset(payload);
    } else {
      reset(initialValues);
    }
  }, [group]);

  const handleClose = () => {
    reset(initialValues);
    clearErrors();
    setGroup(undefined);
    setOpen(false);
  };

  const onSubmit = async (data: MemberGroup) => {
    try {
      let resp;
      if (!group) {
        resp = await createMemberGroup(data);
      } else {
        resp = await updateMemberGroup(data);
      }

      if (resp) {
        handleClose();
        refetch();
      }
    } catch (error: unknown) {
      console.error("Error", { error });
      if (error && typeof error === "object" && "data" in error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description:
            typedError.data?.detail ??
            (typedError.data as { message?: string }).message,
        });
      } else {
        toast({
          variant: "destructive",
          title: "Error in form Submission",
          description: `Something Went Wrong.`,
        });
      }
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={handleClose}>
      <DialogContent className="sm:max-w-[425px]">
        <DialogHeader>
          <DialogTitle>
            {group?.value ? "Edit" : "Create"} Member Group
          </DialogTitle>
        </DialogHeader>
        <FormProvider {...form}>
          <form
            noValidate
            onSubmit={(e) => {
              e.preventDefault();
              e.stopPropagation(); // Add this line to prevent event bubbling
              handleSubmit(onSubmit)(e);
            }}
          >
            <div className="space-y-4">
              <div className="relative ">
                <FloatingLabelInput
                  id="name"
                  placeholder="Enter group name"
                  {...register("name", {
                    required: "Required",
                    validate: (value) => {
                      if (value.trim() === "") {
                        return "Name should not be empty";
                      }
                      return true;
                    },
                    maxLength: {
                      value: 50,
                      message: "Name should be less than 50 characters",
                    },
                  })}
                  error={errors.name?.message}
                />
              </div>

              <LoadingButton
                type="submit"
                loading={isSubmitting}
                disabled={isSubmitting}
                className="w-full flex gap-3"
              >
                {!isSubmitting && <i className="fa fa-floppy-disk"></i>}
                Save
              </LoadingButton>
            </div>
          </form>
        </FormProvider>
      </DialogContent>
    </Dialog>
  );
};
